import { EnvConfig } from '@config';
import request from 'superagent';
import { PayloadArrayFormat } from 'types/wellGuardian';
import { v1 as uuidv1 } from 'uuid';

import { getUserToken } from './tokenManager';

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

class Client {
  baseUrl: string;
  accept: string;
  agent: request.Agent;

  constructor(baseUrl: string, accept = 'application/json') {
    this.baseUrl = baseUrl;
    this.accept = accept;
    this.agent = request.agent();
    this.agent.accept(this.accept);
  }

  async request(
    method: Method,
    endpoint: string,
    data: Record<string, unknown> | Record<symbol, unknown> | PayloadArrayFormat | null = null,
  ) {
    {/* istanbul ignore next */}
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent[method](url);

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    if (this.baseUrl === EnvConfig.drillx_wg_api_url) {
      if (EnvConfig.apim_subscription_key)
        promise = promise.set('Ocp-Apim-Subscription-Key', EnvConfig.apim_subscription_key);

      if (EnvConfig.apim_trace) promise = promise.set('Ocp-Apim-Trace', EnvConfig.apim_trace);
    }

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());

    if (['post', 'put', 'patch'].includes(method) && data) {
      promise = promise.send(data);
    }

    if (this.accept === 'text/html') {
      const response = await promise;

      return response.text;
    }
    const { body } = await promise;

    return body;
  }

  async postFormWithFiles(
    endpoint: string,
    formData?: Record<string, string>,
    files?: Record<string, File[]>,
  ) {
    {/* istanbul ignore next */}
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent.post(url);

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());
    if (formData) {
      for (const key in formData) {
        promise.field(key, formData[key]);
      }
    }

    if (files) {
      for (const key in files) {
        files[key].forEach((file: any) => promise.attach(key, file));
      }
    }

    const { body } = await promise;

    return body;
  }

  async getBlob(endpoint: string): Promise<Blob> {
    {/* istanbul ignore next */}
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent.get(url).responseType('blob');

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());
    const { body } = await promise;

    return body;
  }

  get(endpoint: string) {
    return this.request('get', endpoint);
  }

  post(endpoint: string, data: Record<string, unknown>) {
    return this.request('post', endpoint, data);
  }

  put(endpoint: string, data: Record<string, unknown>) {
    return this.request('put', endpoint, data);
  }

  patch(endpoint: string, data: Record<string, unknown>) {
    return this.request('patch', endpoint, data);
  }

  delete(endpoint: string) {
    return this.request('delete', endpoint);
  }

  post1(endpoint: string, data: PayloadArrayFormat) {
    return this.request('post', endpoint, data);
  }
}

export default Client;
